<div class="modal-header ci-header" hsDraggable="modal-dialog">
  <h4 class="modal-title ci-title">{{ collection?.label }}</h4>
  <span
    class="ci-close"
    role="button"
    tabindex="0"
    title="Close without applying"
    (click)="activeModal.dismiss()"
    (keydown.enter)="activeModal.dismiss()"
  >
    <i class="mdi mdi-close"></i>
  </span>
</div>

<!-- `(scroll)` re-anchors the open value panel: it is on `body`, outside this modal, so a scroll moves
     the cell out from under it. -->
<div class="modal-body ci-body" (scroll)="picker.reanchor()">
  <div class="ci-items">
    <!-- Column labels once, above the repeated lines. -->
    <div class="ci-row ci-row-head">
      <span class="ci-index">#</span>
      <span
        class="ci-cell ci-cell-head"
        *ngFor="let column of collection?.columns"
        [style.flex]="'0 0 ' + width(column) + 'px'"
        [style.width.px]="width(column)"
      >
        {{ column.label }}
        <span class="ci-required" *ngIf="column.required" aria-hidden="true"
          >*</span
        >
      </span>
      <span class="ci-actions"></span>
    </div>

    <div class="ci-row" *ngFor="let row of rows; let i = index">
      <!-- Lines are numbered: a journal entry is discussed by line, not by position on screen. -->
      <span class="ci-index">{{ i + 1 }}</span>

      <span
        class="ci-cell"
        *ngFor="let column of collection.columns"
        [style.flex]="'0 0 ' + width(column) + 'px'"
        [style.width.px]="width(column)"
      >
        <!-- A read-only column wears the same box as the editable ones, shaded and carrying
             the lock the dialog behind this uses. Bare text was the gap: beside a line of
             bordered inputs it reads as a cell that failed to render rather than as one that
             cannot be typed into. -->
        <span class="ci-input-wrap" *ngIf="!column.editable">
          <span
            class="ci-input ci-value-locked"
            [attr.title]="row[column.field] || null"
            >{{ row[column.field] }}</span
          >
          <i class="mdi mdi-lock ci-input-icon" title="Read-only"></i>
        </span>

        <!-- PVAL-26492: an editable column is EDITABLE; a list whose endpoint is not wired yet
             opens an empty picker rather than locking the cell. Same rule as the dialog's rows. -->
        <ng-container *ngIf="column.editable" [ngSwitch]="control(column)">
          <!-- The design's anchored panel, not a native `<select>` — the same control the dialog
               behind this opens, for the same reason. `#cell` is the box it hangs off: here the
               input IS the visible box (it carries its own border), so the panel's border lands on
               the input's line. The trigger shows the LABEL while the panel writes the VALUE. -->
          <span class="ci-input-wrap" *ngSwitchCase="'enum'" #cell>
            <span
              class="ci-input ci-pop-trigger vp-trigger"
              role="button"
              tabindex="0"
              aria-haspopup="listbox"
              [class.ci-input-missing]="column.required && !row[column.field]"
              [attr.aria-expanded]="pickerOpen(column, i)"
              [attr.title]="pickerText(row, column) || null"
              (mousedown)="openPicker(row, column, i, cell, $event)"
              (keydown.enter)="openPicker(row, column, i, cell, $event)"
              (keydown.space)="openPicker(row, column, i, cell, $event)"
              (keydown.escape)="picker.close()"
              >{{ pickerText(row, column) }}</span
            >
            <!-- The chevron reports the panel's state, so it points at where the list currently is. -->
            <i
              class="mdi ci-input-icon"
              [ngClass]="
                pickerOpen(column, i) ? 'mdi-chevron-up' : 'mdi-chevron-down'
              "
            ></i>
          </span>

          <!-- An unbounded reference set, same control as the dialog behind this: a text box that
               asks the resolve endpoint what the typed text matched and offers the answers in the
               anchored panel. A `lookup` column with no `search` list is not searchable and stays
               ordinary free text with a magnifier. -->
          <span class="ci-input-wrap" *ngSwitchCase="'lookup'" #cell>
            <input
              #lookup
              class="ci-input vp-trigger"
              type="text"
              autocomplete="off"
              [attr.placeholder]="
                searchable(column)
                  ? 'Type ' + column.minChars + '+ to search'
                  : 'Type to search'
              "
              [class.ci-input-missing]="column.required && !row[column.field]"
              [value]="lookupText(row, column, i)"
              (input)="onLookupInput(row, column, i, lookup.value, cell)"
              (blur)="onCellCommit(row, column.field)"
            />
            <!-- The glyph reports the search: in flight, nothing found, or neither. -->
            <i
              class="mdi ci-input-icon"
              [ngClass]="
                searchingKey === pickerKey(column, i)
                  ? 'mdi-loading ci-input-spin'
                  : noMatchesKey === pickerKey(column, i)
                  ? 'mdi-magnify-close ci-input-empty'
                  : 'mdi-magnify'
              "
              [attr.title]="
                noMatchesKey === pickerKey(column, i) ? 'No matches' : null
              "
            ></i>
          </span>

          <!-- The app's own date control, not a text box with a calendar glyph painted beside it —
               that had no calendar behind it at all. `nf-datepicker` is what the dialog behind this
               and the bulk grid use: it displays `MM/dd/yyyy` over an ISO value, which is `dateUS`
               exactly, and it is bound to a reactive control (see `dateControl`) because it has no
               value input of its own. -->
          <span class="ci-input-wrap" *ngSwitchCase="'date'">
            <nf-datepicker
              class="ci-datepicker"
              [class.ci-input-missing]="column.required && !row[column.field]"
              [formControl]="dateControl(row, column)"
              [format]="dateDisplayFormat"
              [placeholder]="dateDisplayFormat"
            ></nf-datepicker>
          </span>

          <input
            #num
            *ngSwitchCase="'number'"
            class="ci-input"
            type="text"
            inputmode="decimal"
            [class.ci-input-missing]="column.required && !row[column.field]"
            [value]="row[column.field]"
            (input)="onEdit(row, column.field, num.value)"
            (blur)="onCellCommit(row, column.field)"
          />

          <input
            #line
            *ngSwitchDefault
            class="ci-input"
            type="text"
            [class.ci-input-missing]="column.required && !row[column.field]"
            [value]="row[column.field]"
            (input)="onEdit(row, column.field, line.value)"
            (blur)="onCellCommit(row, column.field)"
          />
        </ng-container>
      </span>

      <!-- Removing a line is a per-line action, so it sits at the end of the line it removes. The
           gutter stays when the collection is locked, so the columns do not shift left. -->
      <span class="ci-actions">
        <i
          *ngIf="!locked"
          class="mdi mdi-trash-can-outline ci-remove"
          role="button"
          tabindex="0"
          [attr.aria-label]="'Remove line ' + (i + 1)"
          (click)="removeRow(i)"
          (keydown.enter)="removeRow(i)"
        ></i>
      </span>
    </div>

    <!-- Σ: a total belongs UNDER the column it totals, on the same geometry as the lines above it,
         not in a band of its own. One [Σ | figure] box beneath each column whose Grouping Logic
         adds, and nothing beneath the rest — a date or a memo stays blank while the amounts
         total. This is the figure the dialog's summary row reconciles against. Absent entirely
         when no column adds (Keywords), rather than an empty row. -->
    <div class="ci-row ci-row-total" *ngIf="hasTotals">
      <span class="ci-index"></span>
      <span
        class="ci-cell"
        *ngFor="let column of collection.columns"
        [style.flex]="'0 0 ' + width(column) + 'px'"
        [style.width.px]="width(column)"
      >
        <span class="ci-total" *ngIf="column.sums" [attr.title]="total(column)">
          <span class="ci-total-sigma">Σ</span>
          <span class="ci-total-figure">{{ total(column) }}</span>
        </span>
      </span>
      <span class="ci-actions"></span>
    </div>

    <!-- Empty state: the columns are still shown above, so the shape of what you are about to add
         is visible before you add it. -->
    <div class="ci-empty" *ngIf="!rows.length">
      No {{ collection?.label?.toLowerCase() }} on this record yet.
    </div>
  </div>
</div>

<div class="modal-footer ci-footer">
  <div class="ci-footer-left">
    <!-- Adding a line is an action on the collection, so it lives with the other actions rather
         than trailing the rows — and it is the collection's to give: only when the summary is
         `edit: true`. Otherwise the same slot says why there is no button. -->
    <button type="button" class="ci-add" *ngIf="!locked" (click)="addRow()">
      <i class="mdi mdi-plus"></i> Add Item
    </button>
    <span class="ci-readonly" *ngIf="locked">
      <i class="mdi mdi-lock"></i> Read-only
    </span>
    <span class="ci-missing" *ngIf="missingRequiredCount as missing">
      <i class="mdi mdi-alert-circle"></i>
      {{ missing }} required value{{ missing === 1 ? '' : 's' }} missing
    </span>
    <span class="ci-dirty" *ngIf="dirty">Unsaved changes</span>
  </div>
  <div class="ci-footer-actions">
    <button nf-button="cancel" (click)="activeModal.dismiss()"></button>
    <button
      nf-button="primary"
      class="ci-done"
      [disabled]="locked"
      [attr.title]="
        locked
          ? collection?.label + ' is not editable — there is nothing to save'
          : null
      "
      (click)="apply()"
    >
      Done
    </button>
  </div>
</div>

<!-- The open value panel, rendered ONCE and outside the line loop: it is positioned against the
     viewport, not against the line, and only one can be open at a time. It moves itself onto `body`,
     so this modal's edges cannot clip it. -->
<hs-value-picker
  *ngIf="picker.open"
  [rows]="picker.rows"
  [columns]="picker.columns"
  [style.left.px]="picker.left"
  [style.top.px]="picker.top"
  [style.width.px]="picker.width"
  (picked)="pickValue($event)"
></hs-value-picker>
